import { OperatorSelector } from "@/shared/selectors/OperatorSelector";
import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { deleteCarrierFromGantt } from "../../repositories/repositories";

export const DeleteModal = ({
	open,
	onHide,
	carrierInstanceId,
	info = { partName: "", sectionName: "", standName: "" },
	suffixTitle,
}: {
	open: boolean;
	onHide: (update: boolean) => void;
	carrierInstanceId: number;
	info: {
		partName?: string | null;
		sectionName?: string | null;
		standName?: string | null;
	};
	suffixTitle?: string;
}) => {
	const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(
		null,
	);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const deleteCarrierInstance = useMutation(
		() => {
			setIsSubmitLoading(true);
			return deleteCarrierFromGantt({
				carrierInstanceId: carrierInstanceId!,
				operatorId: selectedOperatorId!,
			});
		},
		{
			onSuccess: async () => {
				onHide(true);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => {
				onClose();
				setIsSubmitLoading(false);
			},
		},
	);

	const onClose = () => {
		setSelectedOperatorId(null);
	};

	return (
		<>
			{open && <HelmetDexteel title={`Confirm Delete - ${suffixTitle}`} />}
			<MesfModal
				title="CONFIRM DELETE"
				open={open}
				id="confirm-delete"
				maxWidth="sm"
				handleClose={() => onHide(false)}
			>
				<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<p>
								{`Are you sure that you want to delete the carrier: `}
								<strong>{info.partName}</strong>
								{`, with section: `}
								<strong>{info.sectionName}</strong>
								{` and stand: `}
								<strong>{info.standName}</strong>
							</p>
						</Grid2>
						<Grid2 container size={{ xs: 12, md: 12 }} spacing={1}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<OperatorSelector
									value={selectedOperatorId}
									onChange={(value: number) => setSelectedOperatorId(value)}
									label="Operator"
								/>
							</Grid2>
							{!selectedOperatorId && (
								<Grid2 size={{ md: 12, xs: 12 }}>
									<Typography variant="subtitle2" color="textSecondary">
										Select an operator
									</Typography>
								</Grid2>
							)}
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => {
									onClose();
									onHide(false);
								}}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								onClick={() => {
									deleteCarrierInstance.mutate();
								}}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								variant="contained"
								color="primary"
								disabled={!selectedOperatorId || isSubmitLoading}
							>
								Yes
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
